<template>
  <donut-chart-card
    :title="title"
    :value="data"
    :unit="unit"
    :isMoveWrite="isMoveWrite"
    :is-move-detail="isMoveDetail"
    @detail="onClickDetail"
    @write="onClickWrite"
  ></donut-chart-card>
</template>

<script>
/* eslint-disable brace-style */
import DonutChartCard from '@/components/DonutChartCard'

export default {
  name: 'DonutChart101Template',
  components: {DonutChartCard},
  props: {
    dataType: 'board' | 'company' | 'countFields' | 'facilityCategory' | 'field' | 'sum' | 'project',
    title: '',
    boardIds: {
      type: Array,
      default: () => []
    },
    fieldCode: {
      type: String,
      default: () => ''
    },
    unit: {
      type: String,
      default: () => '건'
    },
    graphId: {
      type: Number,
      default: 0
    },
    dateField: {
      type: String,
      default: () => 'created_at'
    },
    countFields: {
      type: Array,
      default: () => []
    },
    sumFields: {
      type: Array,
      default: () => []
    },
    depth: {
      type: Number,
      default: 1
    },
    isMoveDetail: {
      type: Boolean,
      default: true
    },
  },
  computed: {
    projectId () {
      return this.$route.params.proj_id
    },
    /**
     * "자세히 보기" 페이지로 이동할 수 있는지 여부를 반환한다.
     */
    isMoveWrite () {
      // if (Number(`${this.projectId}`) === 1) return false
      // else return true
      return true
    },
    getLevel() {
      const projects = this.$store.state.member.memberInfo.projects;
      if (projects) {
        for (const project of projects) {
          if (`${project.id}` === `${this.projectId}` && project.company.length > 0) {
            const path = project.company[0].pivot.company_path;
            return path.split("/").length - 1; // Level
          }
        }
      }
    },
  },
  data () {
    return {
      data: [{label: '데이터 없음', value: 0}],
      total: 0,
      cache: {},
    }
  },
  async mounted () {
    await this.loadGraph()
  },
  methods: {
    async loadDetailData(currentYear) {
      if (!currentYear) {
        currentYear = new Date().getFullYear();
      }
      const depth = this.depth || 1;
      const cacheKey = `detail_${currentYear}_${depth}`;

      // 캐시에 저장되어 있으면, 캐시에서 불러온다
      if (this.cache[cacheKey]) {
        return JSON.parse(JSON.stringify(this.cache[cacheKey]));
      }
      // 캐시에 저장되어 있지 않으면, API에서 불러온다
      else {
        const params = {
          projectId: this.projectId,
          boardIds: this.boardIds,
          depth,
          currentYear,
          dateField: this.dateField,
          countFields: this.countFields,
          sumFields: this.sumFields,
        };
        if (this.fieldCode) {
          params['fieldCode'] = this.fieldCode;
        }
        const data = await this.$store.dispatch("summary/LOAD_DONUT_101_DETAIL", params);

        this.cache[cacheKey] = data;
        return data;
      }
    },
    async loadGraph () {
      const data = await this.loadDetailData()

      let graph2Data = [];
      // 프로젝트 정보가 지정되지 않았으면, 각 프로젝트에 대한 통계를 표시

      switch (this.dataType) {
      case 'project':
        graph2Data = data.project;
        break;
      case 'board':
        graph2Data = data.board;
        break;
      case 'company':
        graph2Data = data.company;
        break;
      case 'countFields':
        graph2Data = data.countFields;
        break;
      case 'facilityCategory':
        graph2Data = data.facilityCategory;
        break;
      case 'field':
        graph2Data = data.field;
        break;
      case 'sum':
        graph2Data = data.sum;
        break;
      }

      const currentYear = new Date().getFullYear();

      // currentData 아이템들을 누적해서 합산한다
      const sumData = [];
      let rowSum = 0;
      const memberInfo = this.$store.state.member.memberInfo;

      // 기존 다른 타입들의 처리
      const yearData = graph2Data[currentYear];
      let monthlyDataList = [];
      // 13개일 경우, 0번째가 총 합이 들어있다.
      if (yearData.length === 13) {
        monthlyDataList = [yearData[0]];
      } else {
        monthlyDataList = yearData;
      }

      if (this.dataType === 'countFields') {
        // countFields 전용 처리
        for (const monthlyData of Object.values(monthlyDataList)) {
          // monthlyData는 { "body.inspectChecked[*]": { "타워크레인": { label, count }, ... } }
          for (const fieldData of Object.values(monthlyData)) {
            // fieldData는 { "타워크레인": { label, count }, "전기작업": { label, count }, ... }
            for (const [, data] of Object.entries(fieldData)) {
              const label = data.label;
              const count = data.count;
              rowSum += count;

              const foundRow = sumData.find((row) => row.label === label);
              if (foundRow) {
                foundRow.count += count;
              } else {
                sumData.push({label, count});
              }
            }
          }
        }
      } else {
        for (const monthlyData of monthlyDataList) {
          for (const projectData of monthlyData) {
            let label = projectData.label;
            if (label === '경남도청' && parseInt(projectData.company_id) === parseInt(memberInfo.company_id) && memberInfo.section) {
              label = memberInfo.section;
            }

            // custom 데이터가 있는 경우 해당 값들의 합계를 사용
            let count = 0;
            if (projectData.custom && Object.keys(projectData.custom).length > 0) {
              // custom 내의 모든 필드에 대해 count 합산
              for (const fieldData of Object.values(projectData.custom)) {
                if (typeof fieldData === 'object') {
                  // countFields 타입의 데이터인 경우
                  for (const item of Object.values(fieldData)) {
                    count += item.count || 0;
                  }
                } else {
                  // sumFields 타입의 데이터인 경우
                  count += parseFloat(fieldData) || 0;
                }
              }
            } else {
              // custom 데이터가 없는 경우 기존 count 사용
              count = projectData.count;
            }

            rowSum += count;

            const foundRow = sumData.find((row) => row.label === label);
            if (foundRow) {
              foundRow.count += count;
            } else {
              sumData.push({label, count});
            }
          }
        }
      }

      // sumData를 count로 정렬한다
      sumData.sort((a, b) => b.count - a.count);


      const list = [];
      for (const row of sumData) {
        list.push({
          label: row.label,
          value: row.count,
        });
      }

      this.data = list;

      if (this.getLevel === 4 && this.dataType === 'detail') {
        this.total = rowSum;
      } else {
        this.total = data.monthly[currentYear][0];
      }
    },
    onClickDetail () {
      const projectId = this.$route.params.proj_id
      const graphId = this.graphId
      if (projectId) {
        this.$router.push(`/project/${projectId}/graph-detail/${graphId}`)
      } else {
        this.$router.push(`/graph-detail/${graphId}`)
      }
    },

    /**
     * "작성하기" 페이지로 이동한다.
     */
    onClickWrite () {
      const boardId = this.boardIds && this.boardIds.length > 0 ? this.boardIds[0] : 0;
      if (boardId) {
        this.$router.push({name: 'write|tab', params: {proj_id: this.projectId, boardId}})
      } else {
        // 이동할 게시판이 없으므로, 경고 메시지를 표시한다
        alert('대상 문서가 지정되지 않았습니다.');
      }
    }
  },
}
</script>

<style scoped>

</style>
